import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CalendarClock, PhoneCall } from "lucide-react";
import { buyersApi, type BuyerLeadDetailDto, type BuyerNeedsActionReason } from "@/api/buyers";
import { Button, MissingValue, StatusBadge, Timestamp, type StatusTone } from "@/design-system/components/ui";
import { calendarDaysUntil, formatCurrency } from "@/design-system/lib/format";
import { buyerKeys } from "@/hooks/buyers/buyerKeys";
import { useAuth } from "@/hooks/useAuth";
import { buyerLabel } from "@/pages/buyers/buyerFilters";
import { buyerStatusTone, verdictTone } from "@/pages/buyers/buyerListBits";
import { LogContactModal } from "./LogContactModal";
import { ScheduleFollowUpModal } from "./ScheduleFollowUpModal";
import { SpecialistQuickPick } from "./SpecialistQuickPick";

/** The exact string from `Permissions.cs` — the gate on both B2 write endpoints. */
export const BUYERS_UPDATE_PERMISSION = "Buyers.Update";
/** The route gate on `/buyers/advertising` (`App.tsx`). */
const ADVERTISING_READ_PERMISSION = "BuyerAdvertising.Read";

/**
 * D15 — the "Next step" strip: what this buyer needs from the specialist, on
 * every tab, above the tab bar.
 *
 * <p><b>It lives in the record HEADER on purpose.</b> Layout Standard §4.1a keeps
 * workflow actions off an Overview; the header is exempt, because it is chrome
 * on every tab rather than a summary of one. So the two inline verbs — Log
 * contact, Schedule follow-up — sit here and nowhere in the Overview body.</p>
 *
 * <p>🔴 <b>The threshold is READ, never typed.</b> "Stale" means older than the
 * team's `StaleContactDays`, which is per-team and admin-editable, so the
 * strip asks the list stats endpoint for the EFFECTIVE window
 * (`BuyerLeadStatsDto.noContactDays`, resolved as `buyerTeamId ?? caller's
 * team ?? global`). While that read is in flight or has failed there is no
 * number to compare against, and the last-contact date renders untoned rather
 * than against a guessed 14.</p>
 *
 * <p>🔴 <b>The needs-action facts come off the detail record itself.</b>
 * `BuyerLeadDetailDto.needsActionReasons` / `nextActionDueAt` /
 * `nextActionTitle` are computed server-side from the SAME batch facts and the
 * same scalar predicates as the list row (`BuyerLeadService.ToDetail`), against
 * the buyer's OWN team threshold — so this strip and the list can never
 * disagree, and nothing is re-derived client-side. The two modals invalidate
 * `buyerKeys.all`, which refreshes the record along with everything else.</p>
 */
export function BuyerNextStepStrip({ buyer }: { buyer: BuyerLeadDetailDto }) {
  const { hasPermission } = useAuth();
  const canAct = buyer.canEdit && hasPermission(BUYERS_UPDATE_PERMISSION);
  const [action, setAction] = useState<"contact" | "follow-up" | null>(null);

  const statsParams = buyer.buyerTeamId ? { buyerTeamId: buyer.buyerTeamId } : {};
  const stats = useQuery({
    queryKey: buyerKeys.stats(statsParams),
    queryFn: () => buyersApi.stats(statsParams),
  });
  const noContactDays = stats.data?.noContactDays;

  const verdict = buyer.qualification?.overallVerdict ?? "Unknown";
  const nextDue = buyer.nextActionDueAt ?? null;
  const overdue = nextDue != null && (calendarDaysUntil(nextDue) ?? 0) < 0;
  const stale =
    noContactDays != null && isStale(buyer.lastContactAtUtc, noContactDays);
  const reasons = buyer.needsActionReasons ?? [];

  return (
    <section
      aria-label="Next step"
      className="mb-space-4 rounded-md border border-border-light bg-white-warm p-space-3"
      data-testid="buyer-next-step"
    >
      <div className="flex flex-wrap items-start justify-between gap-space-3">
        <dl className="m-0 flex flex-wrap gap-x-space-5 gap-y-space-2">
          <Fact label="Owner" testId="next-step-owner">
            <SpecialistQuickPick
              buyerLeadId={buyer.id}
              currentSpecialistUserId={buyer.assignedSpecialistUserId}
              currentSpecialistName={buyer.assignedSpecialistName}
              canReassign={buyer.canReassign ?? false}
              canUnassign={buyer.canUnassign ?? false}
              buyerDisplayName={buyer.displayName}
              size="sm"
            />
          </Fact>
          <Fact label="Status" testId="next-step-status">
            <StatusBadge tone={buyerStatusTone(buyer.status)}>{buyerLabel(buyer.status)}</StatusBadge>
          </Fact>
          <Fact label="Qualification" testId="next-step-qualification">
            <StatusBadge tone={verdictTone(verdict)}>{buyerLabel(verdict)}</StatusBadge>
          </Fact>
          <Fact label="Capacity" testId="next-step-capacity">
            {buyer.liquidCapital != null || buyer.maxPurchasePrice != null ? (
              <span className="text-small text-onyx">
                {buyer.liquidCapital != null ? formatCurrency(buyer.liquidCapital) : "—"}
                <span className="text-gray-mid"> liquid · </span>
                {buyer.maxPurchasePrice != null ? formatCurrency(buyer.maxPurchasePrice) : "—"}
                <span className="text-gray-mid"> max</span>
              </span>
            ) : (
              <MissingValue />
            )}
          </Fact>
          <Fact label="Last contact" testId="next-step-last-contact">
            {buyer.lastContactAtUtc ? (
              <Timestamp
                value={buyer.lastContactAtUtc}
                className={stale ? "text-status-warning-ink" : "text-onyx"}
              />
            ) : (
              <span className={stale ? "italic text-status-warning-ink" : "italic text-gray-mid"}>
                Never
              </span>
            )}
          </Fact>
          <Fact label="Next follow-up" testId="next-step-follow-up">
            {nextDue ? (
              <span className="min-w-0">
                <Timestamp value={nextDue} className={overdue ? "text-status-error" : "text-onyx"} />
                {buyer.nextActionTitle && (
                  <span className="block truncate text-caption text-gray-mid">{buyer.nextActionTitle}</span>
                )}
              </span>
            ) : (
              <span className="italic text-gray-mid">None scheduled</span>
            )}
          </Fact>
          <Fact label="Advertisement" testId="next-step-advertisement">
            {buyer.advertisementName ? (
              hasPermission(ADVERTISING_READ_PERMISSION) ? (
                <Link to="/buyers/advertising" className="text-small text-gold-shadow hover:underline">
                  {buyer.advertisementName}
                </Link>
              ) : (
                <span className="text-small text-onyx">{buyer.advertisementName}</span>
              )
            ) : (
              <MissingValue />
            )}
          </Fact>
        </dl>

        {canAct && (
          <div className="flex shrink-0 items-center gap-space-2">
            <Button size="sm" variant="secondary" onClick={() => setAction("contact")} data-testid="next-step-log-contact">
              <PhoneCall size={14} strokeWidth={1.5} aria-hidden /> Log contact
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setAction("follow-up")} data-testid="next-step-schedule">
              <CalendarClock size={14} strokeWidth={1.5} aria-hidden /> Schedule follow-up
            </Button>
          </div>
        )}
      </div>

      {reasons.length > 0 && (
        <div className="mt-space-2 flex flex-wrap gap-space-1" data-testid="next-step-reasons">
          {reasons.map((reason) => {
            const badge = reasonBadge(reason, noContactDays);
            return (
              <StatusBadge key={reason} tone={badge.tone}>
                {badge.label}
              </StatusBadge>
            );
          })}
        </div>
      )}

      {action === "contact" && (
        <LogContactModal buyerLeadId={buyer.id} buyerName={buyer.displayName} onClose={() => setAction(null)} />
      )}
      {action === "follow-up" && (
        <ScheduleFollowUpModal buyerLeadId={buyer.id} buyerName={buyer.displayName} onClose={() => setAction(null)} />
      )}
    </section>
  );
}

function Fact({ label, testId, children }: { label: string; testId: string; children: ReactNode }) {
  return (
    <div className="min-w-0" data-testid={testId}>
      <dt className="text-caption uppercase tracking-wide text-gray-mid">{label}</dt>
      <dd className="m-0 mt-space-1 text-small">{children}</dd>
    </div>
  );
}

/** Older than the no-contact window, or never contacted — the list's rule. */
function isStale(lastContact: string | null | undefined, days: number): boolean {
  const ago = calendarDaysUntil(lastContact);
  return ago == null || -ago > days;
}

/** The list's badge vocabulary (§6): red for overdue, amber for attention,
 *  muted for "something to do and nothing is wrong". Mirrors
 *  `BuyersListPage.reasonBadge`, which is not exported. */
function reasonBadge(
  reason: BuyerNeedsActionReason,
  noContactDays: number | undefined,
): { label: string; tone: StatusTone } {
  switch (reason) {
    case "FollowUpOverdue":
      return { label: "Follow-up overdue", tone: "danger" };
    case "FollowUpDueToday":
      return { label: "Follow-up today", tone: "warn" };
    case "NoContact":
      return {
        label: noContactDays != null ? `No contact ${noContactDays}d` : "No recent contact",
        tone: "warn",
      };
    case "NewAssignment":
      return { label: "New assignment", tone: "neutral" };
    case "QualifiedNoSubmission":
      return { label: "Qualified, no submission", tone: "neutral" };
    case "SubmissionAwaitingBuyer":
      return { label: "Awaiting buyer", tone: "neutral" };
    default:
      return { label: buyerLabel(reason), tone: "neutral" };
  }
}
